import axios from "axios";
import { getValidToken } from "../../../utils/authUtils";

const tripApis = {
  createTrip: async (tripData: any) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trips`;
      const token = await getValidToken();
      const response = await axios.post(url, tripData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to create trip");
      }
    } catch (error) {
      console.error("Error creating trip:", error);
      throw error;
    }
  },

  createIncome: async ({
    tripId,
    incomeDetails,
  }: {
    tripId: string;
    incomeDetails: any;
  }) => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/incomes/bulk-payment`;

      const response = await axios.post(url, incomeDetails, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response?.data?.isSuccess) {
        return true;
      }
      return false;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.error("Session expired. Please login again.", "error");
        } else {
          console.error(
            error.response?.data?.message || "Failed to add trip income",
            "error"
          );
        }
      } else {
        console.error("Error adding trip income:", error);
      }
      return false;
    }
  },

  getTrip: async (tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}`;
      const token = await getValidToken();
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to get trip");
      }
    } catch (error) {
      console.error("Error getting trip:", error);
      throw error;
    }
  },

  getVehicle: async (vehicleId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicles/${vehicleId}`;
      const token = await getValidToken();
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to get vehicle");
      }
    } catch (error) {
      console.error("Error getting vehicle:", error);
      throw error;
    }
  },

  getRoute: async (routeId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}master-data/routes/${routeId}`;
      const token = await getValidToken();
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to get route");
      }
    } catch (error) {
      console.error("Error getting route:", error);
      throw error;
    }
  },

  //Expense
  createExpense: async (expenseData: any, tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/bulk`;
      const token = await getValidToken();
      const response = await axios.post(
        url,
        { expenses: expenseData },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to create expense");
      }
    } catch (error) {
      console.error("Error creating expense:", error);
      throw error;
    }
  },
  getAllExpenses: async (tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/expense`;
      const token = await getValidToken();
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to get expenses");
      }
    } catch (error) {
      console.error("Error getting expenses:", error);
      throw error;
    }
  },
  updateExpense: async (expenseData: any, tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/bulk/update`;
      const token = await getValidToken();
      const response = await axios.put(
        url,
        { expenses: expenseData },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to update expense");
      }
    } catch (error) {
      console.error("Error updating expense:", error);
      throw error;
    }
  },
  deleteExpense: async (expenseData: any, tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/bulk/delete`;
      const token = await getValidToken();
      const response = await axios.post(
        url,
        { expenses: expenseData },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to delete expense");
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      throw error;
    }
  },

  createPayment: async (paymentData: any, tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/bulk/create/payments`;
      const token = await getValidToken();
      const response = await axios.post(url, { payments: paymentData }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to create payment");
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      throw error;
    }
  },
  updatePayment: async (paymentData: any, tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/bulk/update/payments`;
      const token = await getValidToken();
      const response = await axios.put(url, { payments: paymentData }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to update payment");
      }
    } catch (error) {
      console.error("Error updating payment:", error);
      throw error;
    }
  },
  deletePayment: async (paymentData: any, tripId: string) => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}trip-expenses/${tripId}/bulk/delete/payments`;
      const token = await getValidToken();
      const response = await axios.post(url, { payments: paymentData }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message || "Failed to delete payment");
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      throw error;
    }
  },
};

export default tripApis;
